import { StreamHandler } from "./stream.handler";
import { SubscribeJackpotProgress, UpdateJackpotProgress } from "@/types";
import type { JackpotProgressSchema } from "@/types";
import { useAppStore } from "@/stores";
import { recomputeJackpots } from "@/stores/derive";
import { announceJackpotWins } from "@/services/jackpot-notifications";

/**
 * `jackpot_progress` — the jackpot-specific volatile stream: each served jackpot campaign's
 * jackpots with their `{active, next, last}` event state (the live pot, the deadline, the last
 * payout). Feeds `jackpotProgressById`; the assembled `jackpotCampaigns` view is re-derived on
 * change.
 *
 * Shared, not per-player: the pot is identical for everyone, which is why the backend gives it its
 * own stream (30 s tick + 5 s event drain) rather than a field on `campaign_progress`. Everything
 * structural about a jackpot (title, kind, ceiling, schedule) rides `campaigns`.
 */
export class JackpotProgressHandler extends StreamHandler<
  JackpotProgressSchema[]
> {
  protected readonly subscribeType = SubscribeJackpotProgress;
  protected readonly updateType = UpdateJackpotProgress;

  protected isBusy(): boolean {
    const s = useAppStore.getState().jackpotProgressStream;
    return s.subscribed || s.loading;
  }
  protected setLoading(loading: boolean): void {
    useAppStore.getState().setJackpotProgressLoading(loading);
  }
  protected setSubscribed(subscribed: boolean): void {
    useAppStore.getState().setJackpotProgressSubscribed(subscribed);
  }
  protected clear(): void {
    useAppStore.getState().clearJackpotProgress();
    recomputeJackpots();
  }
  protected apply(data: JackpotProgressSchema[], initial: boolean): void {
    const previous = useAppStore.getState().jackpotProgressById;
    useAppStore.getState().setJackpotProgress(data);
    recomputeJackpots();
    // Everyone watching hears about a win through this shared stream (the winner gets their own
    // `jackpot_won` toast instead). Live updates only — the subscribe page is history.
    if (!initial) announceJackpotWins(previous, data);
  }
}

export const jackpotProgressHandler = new JackpotProgressHandler();
